import { useEffect, useRef, useState } from "react";
import { Camera, X } from "lucide-react";

// Ported from frontend_Admin/src/components/common/CameraCaptureModal.jsx
// — identical getUserMedia → live <video> preview → canvas-capture
// technique, restyled to this app's neutral palette. Handles the two
// failure modes the task calls out explicitly:
//   - camera unsupported at all (no navigator.mediaDevices/getUserMedia —
//     older browsers, some in-app webviews): detected up front, shown as
//     an error with no retry action, Upload remains available behind this
//     modal once it's closed.
//   - permission denied / no camera device: caught from getUserMedia's own
//     rejection, same friendly message.
// The stream is stopped in every exit path — capture, cancel/close, and
// the effect's own cleanup (covers navigating away while the modal is
// still mounted) — never left running in the background.
export default function CameraCaptureModal({ onCapture, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);

  const unsupported = !navigator.mediaDevices?.getUserMedia;

  useEffect(() => {
    if (unsupported) {
      setError("Camera is not supported on this browser. Please use Upload instead.");
      return;
    }
    let cancelled = false;
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "user" } })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        setReady(true);
      })
      .catch((err) => {
        if (cancelled) return;
        // NotAllowedError = permission denied; NotFoundError = no camera
        // device; anything else falls back to the same generic message.
        if (err?.name === "NotAllowedError") {
          setError("Camera permission was denied. Please allow camera access, or use Upload instead.");
        } else if (err?.name === "NotFoundError") {
          setError("No camera was found on this device. Please use Upload instead.");
        } else {
          setError("Could not access the camera. Please check permissions and try again, or use Upload instead.");
        }
      });
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function capture() {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL("image/png");
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    onCapture(dataUrl);
  }

  function close() {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    onClose();
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-4">
          <h3 className="flex items-center gap-2 font-semibold text-neutral-900">
            <Camera size={18} />
            Take Photo
          </h3>
          <button
            type="button"
            onClick={close}
            aria-label="Close camera"
            className="flex h-7 w-7 items-center justify-center rounded-lg bg-neutral-100 text-neutral-500 hover:bg-neutral-200"
          >
            <X size={15} />
          </button>
        </div>
        <div className="p-5">
          {error ? (
            <p className="rounded-xl bg-red-50 p-4 text-sm text-red-600" role="alert">
              {error}
            </p>
          ) : (
            <div className="relative">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full rounded-xl bg-black"
                style={{ aspectRatio: "4/3" }}
              />
              {!ready && (
                <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/40 text-sm text-white">
                  Starting camera...
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-neutral-200 px-5 py-4">
          <button
            type="button"
            onClick={close}
            className="rounded-lg border border-neutral-300 px-4 py-2.5 text-sm font-medium text-neutral-600 hover:bg-neutral-50"
          >
            Cancel
          </button>
          {!error && (
            <button
              type="button"
              onClick={capture}
              disabled={!ready}
              className="flex items-center gap-1.5 rounded-lg bg-neutral-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-neutral-800 disabled:opacity-50"
            >
              <Camera size={15} />
              Capture
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
